// Faq.jsx — Animated FAQ (no mini-CTA inside rows)
import React, { useRef, useState, useEffect } from "react";

const BRAND = "#0b66c3";

const FAQ_ITEMS = [
  { q: "What is Vertex Suite?", a: "Vertex Suite is an all-in-one WhatsApp Business platform to automate conversations, send notifications, manage templates, take payments, and track analytics — without code." },
  { q: "How is it different from the WhatsApp Business App?", a: "The App is manual for small teams. Vertex Suite uses the WhatsApp Business API to add automation, broadcasts, inbox routing, template management, integrations, and team reporting — built for scale." },
  { q: "Do I need a new WhatsApp number?", a: "Yes. API onboarding needs a number not registered on the personal/Business App. We guide you to register or migrate a fresh number step-by-step." },
  { q: "How long does onboarding take?", a: "Typically 3–7 business days after business verification (Meta) and basic KYC. Our team supports end-to-end onboarding." },
  { q: "Can I take UPI/in-chat payments and integrate my tools?", a: "Yes. Collect UPI/card payments in WhatsApp, and connect Shopify, WooCommerce, Razorpay, Cashfree, Google Sheets, webhooks, or your custom APIs." }
];

/* auto-height for smooth open/close */
function useAutoHeight(isOpen) {
  const ref = useRef(null);
  const [maxH, setMaxH] = useState("0px");
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => setMaxH(isOpen ? `${el.scrollHeight}px` : "0px");
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);
  return [ref, maxH];
}

const Row = ({ id, q, a, openId, setOpenId }) => {
  const isOpen = openId === id;
  const [ref, maxH] = useAutoHeight(isOpen);
  return (
    <div className={`faq-row ${isOpen ? "open" : ""}`}>
      <button
        className="faq-btn"
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        id={`button-${id}`}
        onClick={() => setOpenId(isOpen ? null : id)}
        type="button"
      >
        <span className="qtext"><strong className="qprefix">Q{id + 1}.</strong> {q}</span>
        <span className={`qicon ${isOpen ? "rotate" : ""}`} aria-hidden="true">?</span>
      </button>

      <div id={`panel-${id}`} role="region" aria-labelledby={`button-${id}`} className="faq-panel" style={{ maxHeight: maxH }}>
        <div ref={ref} className="faq-panel-inner">{a}</div>
      </div>
    </div>
  );
};

export default function FAQ() {
  const [openId, setOpenId] = useState(1); // open 2nd by default

  return (
    <section className="faq-wrap">
      <style>{`
        :root{ --brand:${BRAND}; --ink:#0f172a; --muted:#64748b; --border:#e6edf5; }
        .faq-wrap, .faq-wrap *{ box-sizing:border-box; font-family:Inter; }
        .container{ max-width:1100px; margin:0 auto; padding:0 20px; }
        .faq-head{ text-align:center; margin: 0 0 14px; }
        .faq-title{ margin:0; font-size:36px; font-weight:900; color:var(--ink); }
        .faq-tag{ margin:8px 0 0; font-size:18px; color:var(--muted); }
        .brand{ color:var(--brand); font-weight:900; }

        .faq-card{ border-radius:18px; padding:16px; background:#fff; border:1px solid var(--border); box-shadow:0 10px 26px rgba(2,6,23,.06); }

        .faq-row{
          background:#fff; border:1px solid var(--border); border-radius:12px; margin:10px 0; overflow:hidden;
          transition: border-color .2s ease, transform .12s ease, box-shadow .2s ease, background .2s ease;
        }
        .faq-row:hover{ border-color:#cfe1ff; transform: translateY(-1px); box-shadow:0 10px 22px rgba(2,6,23,.06); background:#fbfdff; }
        .faq-row.open{ border-color: var(--brand); box-shadow: 0 12px 26px rgba(11,102,195,.18); background: linear-gradient(0deg,#f5f9ff,#ffffff); }

        .faq-btn{
          width:100%; text-align:left; background:transparent; border:none; color:var(--ink);
          padding: 18px 18px 18px 20px; display:flex; align-items:center; justify-content:space-between; gap:14px; cursor:pointer;
          font-weight:800; letter-spacing:.2px;
        }
        .qtext{ font-size: 1.02rem; }
        .qprefix{ color:var(--brand); margin-right:6px; }

        .qicon{
          width:28px; height:28px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center;
          font-weight:900; color:#ffffff;
          background: linear-gradient(135deg, var(--brand), #5aa4ff);
          box-shadow: 0 4px 12px rgba(11,102,195,.28);
          transition: transform .25s ease, filter .2s ease;
        }
        .qicon.rotate{ transform: rotate(180deg); filter: brightness(.96); }

        .faq-panel{ overflow:hidden; transition: max-height .35s ease; background:#f8fafc; border-top:1px solid var(--border); }
        .faq-panel-inner{ padding: 14px 20px 18px; color:#334155; line-height:1.65; }

        @media (max-width:640px){
          .faq-title{ font-size:28px; }
        }
      `}</style>

      <div className="container">
        <div className="faq-head">
          <h3 className="faq-title">Frequently asked questions</h3>
          <p className="faq-tag">
            Quick answers on <span className="brand">Vertex Suite</span> — automation, templates, pricing and use-cases.
          </p>
        </div>

        <div className="faq-card">
          {FAQ_ITEMS.map((item, i) => (
            <Row key={i} id={i} q={item.q} a={item.a} openId={openId} setOpenId={setOpenId} />
          ))}
        </div>
      </div>
    </section>
  );
}
